import type { AISettings, GeneralSettings, UISettings } from "@follow/shared/settings/interface"
import type { SpotlightSettings } from "@follow/shared/spotlight"
import {
  mergeAppearancePayloadWithSpotlightSettings,
  pickSpotlightPayloadFromRemoteAppearance,
  toAppearanceSpotlightPayload,
} from "@follow/shared/spotlight"
import { registerSyncModel } from "@follow/store/sync/model-registry"
import type { SyncAction } from "@follow/store/sync/types"
import { whoami } from "@follow/store/user/getters"
import { tracker } from "@follow/tracker"
import { EventBus } from "@follow/utils/event-bus"
import { getStorageNS } from "@follow/utils/ns"
import { isEmptyObject, sleep } from "@follow/utils/utils"
import type { SettingsTab } from "@follow-app/client-sdk"
import { FollowAPIError } from "@follow-app/client-sdk"
import { isEqual } from "es-toolkit"
import type { PrimitiveAtom } from "jotai"

import {
  __aiSettingAtom,
  aiLocalOnlyKeys,
  createDefaultSettings as createDefaultAISettings,
  getAISettings,
  hasOnlyUnchangedServerShortcuts,
} from "~/atoms/settings/ai"
import {
  __generalSettingAtom,
  createDefaultGeneralSettings,
  generalLocalOnlyKeys,
  getGeneralSettings,
} from "~/atoms/settings/general"
import {
  __spotlightSettingAtom,
  getSpotlightSettings,
  spotlightServerSyncWhiteListKeys,
} from "~/atoms/settings/spotlight"
import {
  __uiSettingAtom,
  createDefaultUISettings,
  getUISettings,
  uiLocalOnlyKeys,
} from "~/atoms/settings/ui"
import { followClient } from "~/lib/api-client"
import { jotaiStore } from "~/lib/jotai"
import { queryClient } from "~/lib/query-client"
import { settings } from "~/queries/settings"

type SettingMapping = {
  appearance: UISettings
  general: GeneralSettings
  ai: AISettings
  spotlight: SpotlightSettings
}
type SettingDomain = keyof SettingMapping
type RemoteSettingsTab = "appearance" | "general" | "ai"
export interface RemoteSettingsResponse {
  code: 0
  settings: Record<string, any>
  updated: Record<string, string>
}

const pickSyncPayload = <T extends object>(payload: T, keys: readonly (keyof T | string)[]) => {
  const nextPayload = {} as Partial<T>
  const record = payload as Record<string, unknown>

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      nextPayload[key as keyof T] = record[key as string] as T[keyof T]
    }
  }

  return nextPayload
}

const localSettingGetterMap = {
  appearance: () => getUISettings(),
  general: () => getGeneralSettings(),
  ai: () => getAISettings(),
  spotlight: () => getSpotlightSettings(),
}

const createInternalSetter =
  <T>(atom: PrimitiveAtom<T>) =>
  (payload: Partial<T>) => {
    const current = jotaiStore.get(atom)
    jotaiStore.set(atom, { ...current, ...payload })
  }

const localSettingSetterMap = {
  appearance: createInternalSetter(__uiSettingAtom),
  general: createInternalSetter(__generalSettingAtom),
  ai: createInternalSetter(__aiSettingAtom),
  spotlight: createInternalSetter(__spotlightSettingAtom),
}

type LocalOnlyDomain = Exclude<SettingDomain, "spotlight">

/**
 * Appearance, general and AI settings sync every key except their device-local ones.
 * Spotlight rules travel in the appearance tab and sync only their own keys, so the
 * appearance domain leaves those keys to them.
 */
const localOnlyKeysMap: Record<LocalOnlyDomain, readonly string[]> = {
  appearance: [...uiLocalOnlyKeys, "spotlights", "spotlightsUpdated"],
  general: generalLocalOnlyKeys,
  ai: aiLocalOnlyKeys,
}

const defaultSettingsGetterMap: Record<LocalOnlyDomain, () => object> = {
  appearance: createDefaultUISettings,
  general: createDefaultGeneralSettings,
  ai: createDefaultAISettings,
}

/**
 * Whether a setting of this device holds nothing the user changed here. AI shortcuts that are
 * only the server's, unchanged, hold nothing either: every device adds those on its own, signed
 * in or not, and sending them would replace the account's own shortcuts.
 */
const isSettingAtDefault = (
  domain: LocalOnlyDomain,
  key: string,
  localValue: unknown,
  defaultValue: unknown,
) =>
  isEqual(localValue, defaultValue) ||
  (domain === "ai" &&
    key === "shortcuts" &&
    Array.isArray(localValue) &&
    hasOnlyUnchangedServerShortcuts(localValue))

const omitLocalOnlyKeys = <T extends object>(domain: LocalOnlyDomain, payload: T) => {
  const localOnlyKeys = new Set(localOnlyKeysMap[domain])
  return Object.fromEntries(
    // The server records when each tab changed, so `updated` stays on the device as well.
    Object.entries(payload).filter(([key]) => key !== "updated" && !localOnlyKeys.has(key)),
  ) as Partial<T>
}

const remoteTabMap: Record<SettingDomain, RemoteSettingsTab> = {
  appearance: "appearance",
  general: "general",
  ai: "ai",
  spotlight: "appearance",
}

const remoteTabToDomainMap: Record<RemoteSettingsTab, SettingDomain[]> = {
  appearance: ["appearance", "spotlight"],
  general: ["general"],
  ai: ["ai"],
}

const bizSettingKeyToTabMapping = {
  ui: "appearance",
  general: "general",
  ai: "ai",
  spotlight: "spotlight",
} as const

const getSettingUpdated = (payload: Record<string, unknown>) =>
  typeof payload.updated === "number" ? payload.updated : undefined

const getLocalUpdated = (payload: object) =>
  typeof (payload as { updated?: unknown }).updated === "number"
    ? (payload as { updated: number }).updated
    : undefined

const getLocalAppearancePayload = () =>
  omitLocalOnlyKeys("appearance", localSettingGetterMap.appearance())

const getLocalSpotlightSnapshot = () =>
  localSettingGetterMap.spotlight() as SpotlightSettings & { updated?: number }

const payloadForRemote = (
  domain: SettingDomain,
  payload: Record<string, unknown>,
): Record<string, unknown> =>
  domain === "spotlight"
    ? toAppearanceSpotlightPayload(
        payload as unknown as SpotlightSettings,
        getSettingUpdated(payload),
      )
    : omitLocalOnlyKeys(domain, payload)

const buildFullLocalAppearancePayload = () => {
  const spotlightSettings = getLocalSpotlightSnapshot()
  return mergeAppearancePayloadWithSpotlightSettings(
    getLocalAppearancePayload(),
    spotlightSettings,
    getSettingUpdated(spotlightSettings as unknown as Record<string, unknown>),
  )
}

const mergeAppearancePayloadWithLocalChanges = (
  basePayload: Record<string, unknown>,
  options: {
    appearancePayload?: Record<string, unknown>
    spotlightSettings?: (SpotlightSettings & { updated?: number }) | null
  },
) => {
  let nextPayload = { ...basePayload }

  if (options.appearancePayload) {
    nextPayload = {
      ...nextPayload,
      ...options.appearancePayload,
    }
  }

  if (options.spotlightSettings) {
    nextPayload = mergeAppearancePayloadWithSpotlightSettings(
      nextPayload,
      options.spotlightSettings,
      getSettingUpdated(options.spotlightSettings as unknown as Record<string, unknown>),
    )
  }

  return nextPayload
}

const getLocalPayloadForRemoteTab = (remoteTab: Exclude<RemoteSettingsTab, "appearance">) =>
  payloadForRemote(
    remoteTab,
    localSettingGetterMap[remoteTab]() as unknown as Record<string, unknown>,
  )

const payloadFromRemote = (
  domain: SettingDomain,
  payload: Record<string, unknown>,
  updated: number,
): Record<string, unknown> | null => {
  if (domain === "spotlight") {
    return pickSpotlightPayloadFromRemoteAppearance(payload, updated)
  }

  const nextPayload = omitLocalOnlyKeys(domain, payload)
  if (isEmptyObject(nextPayload)) {
    return null
  }

  return {
    ...nextPayload,
    updated,
  }
}

const isUnauthorizedError = (error: unknown) => {
  if (error instanceof FollowAPIError) {
    return error.status === 401
  }

  if (error && typeof error === "object" && "status" in error) {
    return Number((error as { status?: unknown }).status) === 401
  }

  return false
}

export type SettingSyncTab = SettingDomain
export interface SettingSyncQueueItem<T extends SettingSyncTab = SettingSyncTab> {
  tab: T
  payload: Partial<SettingMapping[T]>
  date: number
}

interface PersistedSettingSyncQueue {
  ownerUserId: string | null
  queue: SettingSyncQueueItem[]
}

const BASE_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 60_000
/** Past this many failures in a row the retry delay stays at its maximum. */
const MAX_BACKOFF_EXPONENT = 16

class SettingSyncQueue {
  queue: SettingSyncQueueItem[] = []
  private ownerUserId: string | null = null

  private getCurrentUserId() {
    return whoami()?.id ?? null
  }

  private bindQueueOwner(currentUserId: string) {
    if (this.ownerUserId === null) {
      this.ownerUserId = currentUserId
      return
    }

    if (this.ownerUserId !== currentUserId) {
      this.ownerUserId = currentUserId
      this.queue = []
    }
  }

  private reportSyncError(stage: "flush" | "syncLocal", error: unknown) {
    void tracker.manager.captureException(error, {
      module: "setting_sync",
      stage,
    })
  }

  private disposers: (() => void)[] = []
  async init() {
    this.teardown()

    this.load()
    // Changes left from the last session are only sent by a flush
    if (this.queue.length > 0 && this.getCurrentUserId()) {
      this.scheduleFlush()
    }
    this.uploadLocalSettingsOnce()

    const d1 = EventBus.subscribe("SETTING_CHANGE_EVENT", (data) => {
      const currentUserId = this.getCurrentUserId()
      if (!currentUserId) return

      this.bindQueueOwner(currentUserId)

      const tab = bizSettingKeyToTabMapping[data.key]
      if (!tab) return

      const nextPayload =
        tab === "spotlight"
          ? pickSyncPayload(data.payload, spotlightServerSyncWhiteListKeys)
          : omitLocalOnlyKeys(tab, data.payload)
      if (isEmptyObject(nextPayload)) return
      this.enqueue(tab, nextPayload)
    })
    // Coming back to the app or the network retries a change that failed to send at once.
    const resumeHandler = () => this.resume()
    const visibilityHandler = () => {
      if (!document.hidden) this.resume()
    }
    window.addEventListener("online", resumeHandler)
    window.addEventListener("focus", resumeHandler)
    document.addEventListener("visibilitychange", visibilityHandler)
    const d2 = () => {
      window.removeEventListener("online", resumeHandler)
      window.removeEventListener("focus", resumeHandler)
      document.removeEventListener("visibilitychange", visibilityHandler)
    }

    const unloadHandler = () => this.persist()

    window.addEventListener("beforeunload", unloadHandler)
    const d3 = () => window.removeEventListener("beforeunload", unloadHandler)

    this.disposers.push(d1, d2, d3)
  }

  teardown() {
    for (const disposer of this.disposers) {
      disposer()
    }
    this.disposers = []
    this.clearRetryTimer()
    this.retryAttempts = 0
    this.queue = []
    this.ownerUserId = null
  }

  /**
   * The network, the API or the app came back: send what is waiting now instead of at the
   * end of the back-off, which starts over.
   */
  resume() {
    this.retryAttempts = 0
    this.clearRetryTimer()
    if (this.queue.length > 0 && this.getCurrentUserId()) {
      this.scheduleFlush()
    }
  }

  private readonly storageKey = getStorageNS("setting_sync_queue")
  private persist() {
    if (this.queue.length === 0) {
      localStorage.removeItem(this.storageKey)
      return
    }

    const payload: PersistedSettingSyncQueue = {
      ownerUserId: this.ownerUserId,
      queue: this.queue,
    }
    localStorage.setItem(this.storageKey, JSON.stringify(payload))
  }

  private load() {
    const queue = localStorage.getItem(this.storageKey)
    localStorage.removeItem(this.storageKey)
    if (!queue) {
      return
    }

    const currentUserId = this.getCurrentUserId()

    try {
      const parsed = JSON.parse(queue) as unknown
      if (Array.isArray(parsed)) {
        // Backward compatibility: legacy versions persisted the queue array directly.
        this.queue = parsed
        this.ownerUserId = currentUserId
      } else if (!parsed || typeof parsed !== "object") {
        this.queue = []
        this.ownerUserId = null
        return
      } else {
        const payload = parsed as Partial<PersistedSettingSyncQueue>
        this.queue = Array.isArray(payload.queue) ? payload.queue : []
        if (typeof payload.ownerUserId === "string" || payload.ownerUserId === null) {
          this.ownerUserId = payload.ownerUserId
        } else {
          // Backward compatibility for payloads without owner information.
          this.ownerUserId = currentUserId
        }
      }
    } catch {
      /* empty */
    }

    if (!currentUserId) {
      return
    }

    this.bindQueueOwner(currentUserId)
  }

  private chain = Promise.resolve()

  private threshold = 1000
  private flushScheduled = false
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  /** Failed flushes in a row; they set the delay before the next try. */
  private retryAttempts = 0

  private pendingPromise: Promise<RemoteSettingsResponse> | null = null

  private fetchSettingRemote() {
    if (this.pendingPromise) {
      return this.pendingPromise
    }

    const settingQuery = settings.get()
    const promise = queryClient.fetchQuery({
      queryKey: settingQuery.key,
      queryFn: settingQuery.fn,
      staleTime: 0,
    }) as Promise<RemoteSettingsResponse>

    this.pendingPromise = promise.finally(() => {
      this.pendingPromise = null
    })

    return this.pendingPromise
  }

  private async fetchRemoteAppearancePayload() {
    const remoteSettings = await this.fetchSettingRemote()
    const remoteAppearancePayload = remoteSettings.settings?.appearance

    return remoteAppearancePayload && typeof remoteAppearancePayload === "object"
      ? { ...(remoteAppearancePayload as Record<string, unknown>) }
      : {}
  }

  private async fetchFreshRemoteSettings() {
    return followClient.api.settings.get() as Promise<RemoteSettingsResponse>
  }

  async enqueue<T extends SettingSyncTab>(tab: T, payload: Partial<SettingMapping[T]>) {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) {
      return
    }

    this.bindQueueOwner(currentUserId)

    const now = Date.now()
    if (isEmptyObject(payload)) {
      return
    }
    this.queue.push({
      tab,
      payload,
      date: now,
    })

    this.scheduleFlush()
  }

  private scheduleFlush() {
    // A pending retry sends every change made until then; it is brought forward by `resume`.
    if (this.flushScheduled || this.retryTimer) {
      return
    }

    this.flushScheduled = true
    this.chain = this.chain
      .finally(() => sleep(this.threshold))
      .finally(async () => {
        const flushedItems = new Set(this.queue)
        let sent = false
        try {
          sent = await this.flush()
        } finally {
          this.flushScheduled = false
          // A change made while the flush awaited the server was not part of it, and its
          // enqueue skipped scheduling because this flush was still pending
          if (this.queue.some((item) => !flushedItems.has(item))) {
            this.scheduleFlush()
          } else if (!sent && this.queue.length > 0) {
            this.scheduleRetry()
          }
        }
      })
  }

  /** Try a failed flush again later, waiting longer after each failure in a row. */
  private scheduleRetry() {
    this.clearRetryTimer()
    this.retryAttempts += 1
    const exponent = Math.min(MAX_BACKOFF_EXPONENT, this.retryAttempts - 1)
    const delay = Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** exponent)
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.scheduleFlush()
    }, delay)
  }

  private clearRetryTimer() {
    if (!this.retryTimer) return
    clearTimeout(this.retryTimer)
    this.retryTimer = null
  }

  /**
   * Send the queued changes. Resolves to `false` when a request failed and the changes are
   * still waiting, so the caller tries again later; anything else needs no retry.
   */
  private async flush(): Promise<boolean> {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) {
      return true
    }

    this.bindQueueOwner(currentUserId)

    // The `online` event resumes the queue.
    if (navigator.onLine === false) {
      return true
    }

    const changedDomainsByRemoteTab = {} as Partial<Record<RemoteSettingsTab, Set<SettingDomain>>>
    const domainOverrides = {} as Partial<Record<SettingDomain, Record<string, unknown>>>
    const referenceMap = {} as Partial<Record<RemoteSettingsTab, Set<SettingSyncQueueItem>>>
    for (const item of this.queue) {
      const remoteTab = remoteTabMap[item.tab]
      changedDomainsByRemoteTab[remoteTab] ||= new Set()
      changedDomainsByRemoteTab[remoteTab].add(item.tab)

      referenceMap[remoteTab] ||= new Set()
      referenceMap[remoteTab].add(item)

      domainOverrides[item.tab] = {
        ...domainOverrides[item.tab],
        ...payloadForRemote(item.tab, item.payload as unknown as Record<string, unknown>),
      }
    }

    let remoteAppearancePayload = {} as Record<string, unknown>
    if (changedDomainsByRemoteTab.appearance?.size) {
      try {
        remoteAppearancePayload = await this.fetchRemoteAppearancePayload()
      } catch (error) {
        if (isUnauthorizedError(error)) {
          this.queue = []
          this.ownerUserId = currentUserId
          return true
        }

        this.reportSyncError("flush", error)
        return false
      }
    }

    const promises = [] as Promise<any>[]
    for (const tab in changedDomainsByRemoteTab) {
      const remoteTab = tab as RemoteSettingsTab
      const changedDomains = changedDomainsByRemoteTab[remoteTab]
      const json =
        remoteTab === "appearance"
          ? mergeAppearancePayloadWithLocalChanges(remoteAppearancePayload, {
              appearancePayload: changedDomains?.has("appearance")
                ? domainOverrides.appearance
                : undefined,
              spotlightSettings: changedDomains?.has("spotlight")
                ? getLocalSpotlightSnapshot()
                : undefined,
            })
          : domainOverrides[remoteTab]

      if (!json || isEmptyObject(json)) {
        continue
      }

      const promise = followClient.api.settings
        .update({
          tab: remoteTab as SettingsTab,
          ...json,
        })
        .then(() => {
          // remove from queue
          for (const item of referenceMap[remoteTab] ?? []) {
            const index = this.queue.indexOf(item)
            if (index !== -1) {
              this.queue.splice(index, 1)
            }
          }
        })
      // A tab that failed stays queued and is sent again by the retry
      promises.push(promise)
    }

    try {
      await Promise.all(promises)
      this.retryAttempts = 0
      return true
    } catch (error) {
      if (isUnauthorizedError(error)) {
        this.queue = []
        this.ownerUserId = currentUserId
        return true
      }

      this.reportSyncError("flush", error)
      return false
    }
  }

  private replaceAllRemote() {
    return Promise.all([
      followClient.api.settings.update({
        tab: "appearance",
        ...buildFullLocalAppearancePayload(),
      }),
      followClient.api.settings.update({
        tab: "general",
        ...getLocalPayloadForRemoteTab("general"),
      }),
      followClient.api.settings.update({
        tab: "ai",
        ...getLocalPayloadForRemoteTab("ai"),
      }),
    ])
  }

  replaceRemote(tab?: SettingSyncTab) {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) {
      return this.chain
    }

    this.bindQueueOwner(currentUserId)

    if (!tab) {
      this.chain = this.chain.finally(() => this.replaceAllRemote())
      return this.chain
    } else {
      this.chain = this.chain.finally(async () => {
        const remoteTab = remoteTabMap[tab]
        const payload =
          remoteTab === "appearance"
            ? mergeAppearancePayloadWithLocalChanges(await this.fetchRemoteAppearancePayload(), {
                appearancePayload: tab === "appearance" ? getLocalAppearancePayload() : undefined,
                spotlightSettings: tab === "spotlight" ? getLocalSpotlightSnapshot() : undefined,
              })
            : getLocalPayloadForRemoteTab(remoteTab)

        return followClient.api.settings.update({
          tab: remoteTab as SettingsTab,
          ...payload,
        })
      })

      return this.chain
    }
  }

  replaceRemoteIfEmpty() {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) {
      return this.chain
    }

    this.bindQueueOwner(currentUserId)

    this.chain = this.chain.finally(async () => {
      try {
        const remoteSettings = await this.fetchFreshRemoteSettings()
        if (!isEmptyObject(remoteSettings.settings)) {
          return
        }

        await this.replaceAllRemote()
      } catch (error) {
        if (isUnauthorizedError(error)) {
          this.queue = []
          this.ownerUserId = currentUserId
          return
        }

        this.reportSyncError("flush", error)
      }
    })

    return this.chain
  }

  /**
   * A local change to this tab is still waiting to be sent. It wins for now, and the server's
   * answer to it is logged with both changes merged.
   */
  private hasPendingChange(tab: RemoteSettingsTab) {
    return this.queue.some((item) => remoteTabMap[item.tab] === tab)
  }

  /**
   * From 1.6.0 until this version only the device-local keys synced, so what the server holds
   * for every other setting may be months old. Once per account, before this device takes any
   * of it, the settings changed here from their defaults are queued for upload. While they
   * wait, their tabs keep the server's values out; afterwards the settings still at their
   * defaults take the account's values.
   */
  private uploadLocalSettingsOnce() {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) return

    const doneKey = getStorageNS(`setting_sync_local_first_${currentUserId}`)
    if (localStorage.getItem(doneKey)) return
    localStorage.setItem(doneKey, "1")

    for (const domain of Object.keys(defaultSettingsGetterMap) as LocalOnlyDomain[]) {
      const localSettings = localSettingGetterMap[domain]() as unknown as Record<string, unknown>
      const defaultSettings = defaultSettingsGetterMap[domain]() as Record<string, unknown>
      const changedSettings = Object.fromEntries(
        Object.keys(defaultSettings)
          .filter(
            (key) => !isSettingAtDefault(domain, key, localSettings[key], defaultSettings[key]),
          )
          .map((key) => [key, localSettings[key]]),
      )
      const payload = omitLocalOnlyKeys(domain, changedSettings)
      if (!isEmptyObject(payload)) {
        void this.enqueue(domain, payload)
      }
    }
  }

  applyRemoteSettings(remoteSettings: RemoteSettingsResponse) {
    this.uploadLocalSettingsOnce()
    if (isEmptyObject(remoteSettings.settings)) return

    for (const tab in remoteSettings.settings) {
      const remoteTab = tab as RemoteSettingsTab
      if (this.hasPendingChange(remoteTab)) continue

      const remoteSettingPayload = remoteSettings.settings[remoteTab]
      const updated = remoteSettings.updated[remoteTab]

      if (!updated) {
        continue
      }

      const remoteUpdatedDate = new Date(updated).getTime()

      for (const domain of remoteTabToDomainMap[remoteTab] ?? []) {
        const localSettings = localSettingGetterMap[domain]()
        const localSettingsUpdated = getLocalUpdated(localSettings)

        if (localSettingsUpdated && remoteUpdatedDate <= localSettingsUpdated) {
          continue
        }

        const nextPayload = payloadFromRemote(domain, remoteSettingPayload, remoteUpdatedDate)
        if (!nextPayload || isEmptyObject(nextPayload)) {
          continue
        }

        const setter = localSettingSetterMap[domain]
        setter(nextPayload)
      }
    }
  }

  /**
   * One settings tab changed on the server, reported by the sync engine's change log. The
   * action carries what `GET /settings` would return for that tab, so it goes through the
   * same last-writer-wins merge as a full sync.
   */
  async applyRemoteChange(action: SyncAction) {
    if (action.action !== "U" || !action.modelId) return
    const tab = action.modelId as RemoteSettingsTab
    const data = action.data as { payload?: Record<string, unknown>; updatedAt?: string } | null
    if (!data?.updatedAt) return

    this.uploadLocalSettingsOnce()
    if (this.hasPendingChange(tab)) return

    if (!data.payload) {
      // Tabs that hold credentials are logged without their payload: read them the usual way.
      await this.syncLocal()
      return
    }

    const { payload, updatedAt } = data
    this.applyRemoteSettings({
      code: 0,
      settings: { [tab]: payload },
      updated: { [tab]: updatedAt },
    })
    // Keep the cached `/settings` response in step for the settings dialog.
    queryClient.setQueryData<RemoteSettingsResponse>(settings.get().key, (current) =>
      current
        ? {
            ...current,
            settings: { ...current.settings, [tab]: payload },
            updated: { ...current.updated, [tab]: updatedAt },
          }
        : current,
    )
  }

  /**
   * @param rethrow let a failed request reach the caller. The sync engine needs it: a load
   * that did not happen must not count as done.
   */
  async syncLocal(options?: { rethrow?: boolean }) {
    const currentUserId = this.getCurrentUserId()
    if (!currentUserId) return

    this.bindQueueOwner(currentUserId)

    const remoteSettings = await this.fetchSettingRemote().catch((error) => {
      if (isUnauthorizedError(error)) {
        this.queue = []
        this.ownerUserId = currentUserId
        return null
      }

      this.reportSyncError("syncLocal", error)
      if (options?.rethrow) throw error
      return null
    })

    if (!remoteSettings) return

    this.applyRemoteSettings(remoteSettings)
  }
}

export const settingSyncQueue = new SettingSyncQueue()

// Registered at module load, before the sync engine starts: once the settings were loaded in
// full, launches no longer request them and other devices' changes arrive through the log.
registerSyncModel("setting", {
  bootstrap: () => settingSyncQueue.syncLocal({ rethrow: true }),
  apply: (action) => settingSyncQueue.applyRemoteChange(action),
})
